import React, { useState, useEffect, useCallback } from "react";
import { format, subDays } from "date-fns";
import { es } from "date-fns/locale";

import {
  makeStyles,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  ButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import GetAppIcon from "@material-ui/icons/GetApp";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import PeopleIcon from "@material-ui/icons/People";
import ChatIcon from "@material-ui/icons/Chat";
import AssignmentIcon from "@material-ui/icons/Assignment";
import SendIcon from "@material-ui/icons/Send";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { exportToExcel, exportToPDF } from "./exportUtils";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  kpiCard: {
    height: "100%",
    position: "relative",
    overflow: "visible",
  },
  kpiContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  kpiIcon: {
    fontSize: 48,
    opacity: 0.3,
  },
  kpiValue: {
    fontSize: "2rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  chartPaper: {
    padding: theme.spacing(2),
    height: 380,
  },
  tablePaper: {
    padding: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  exportButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  quickFilterBtn: {
    textTransform: "none",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
}));

const COLORS = ["#2196F3", "#FF9800", "#4CAF50", "#F44336", "#9C27B0", "#00BCD4"];
const STATUS_COLORS = {
  open: "#FF9800",
  pending: "#2196F3",
  closed: "#4CAF50",
};
const STATUS_LABELS = {
  open: "Abierto",
  pending: "Pendiente",
  closed: "Cerrado",
};

const Reports = () => {
  const classes = useStyles();

  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [ticketsByDay, setTicketsByDay] = useState([]);
  const [ticketsByUser, setTicketsByUser] = useState([]);
  const [messagesByDay, setMessagesByDay] = useState([]);
  const [detailedTickets, setDetailedTickets] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load users for filter
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users");
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { dateFrom, dateTo };
      if (selectedUser) params.userId = selectedUser;
      if (selectedStatus) params.status = selectedStatus;

      const [overviewRes, ticketsDayRes, ticketsUserRes, messagesDayRes, detailedRes] =
        await Promise.all([
          api.get("/reports/overview", { params }),
          api.get("/reports/tickets-by-day", { params }),
          api.get("/reports/tickets-by-user", { params }),
          api.get("/reports/messages-by-day", { params }),
          api.get("/reports/detailed-tickets", { params }),
        ]);

      setOverview(overviewRes.data);
      setTicketsByDay(ticketsDayRes.data);
      setTicketsByUser(ticketsUserRes.data);
      setMessagesByDay(messagesDayRes.data);
      setDetailedTickets(detailedRes.data);
      setPage(0);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [dateFrom, dateTo, selectedUser, selectedStatus]);

  // Load on mount
  useEffect(() => {
    fetchReports();
  }, []);

  const setQuickDate = (days) => {
    setDateFrom(format(subDays(new Date(), days), "yyyy-MM-dd"));
    setDateTo(format(new Date(), "yyyy-MM-dd"));
  };

  const pieData = overview
    ? [
        { name: "Abiertos", value: overview.totalOpen },
        { name: "Pendientes", value: overview.totalPending },
        { name: "Cerrados", value: overview.totalClosed },
      ].filter((d) => d.value > 0)
    : [];

  const handleExportExcel = () => {
    exportToExcel({
      dateFrom,
      dateTo,
      userId: selectedUser,
      status: selectedStatus,
    });
  };

  const handleExportPDF = () => {
    exportToPDF({
      dateFrom,
      dateTo,
      userId: selectedUser,
      status: selectedStatus,
    });
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Reportes</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleExportExcel}
            disabled={!overview}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            disabled={!overview}
          >
            PDF
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Fecha desde"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Fecha hasta"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Agente</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                label="Agente"
              >
                <MenuItem value="">Todos</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="open">Abierto</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="closed">Cerrado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={fetchReports}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              disabled={loading}
            >
              Generar
            </Button>
          </Grid>
          <Grid item xs={12}>
            <ButtonGroup size="small" variant="text" color="primary">
              <Button className={classes.quickFilterBtn} onClick={() => setQuickDate(7)}>
                7 dias
              </Button>
              <Button className={classes.quickFilterBtn} onClick={() => setQuickDate(15)}>
                15 dias
              </Button>
              <Button className={classes.quickFilterBtn} onClick={() => setQuickDate(30)}>
                30 dias
              </Button>
              <Button className={classes.quickFilterBtn} onClick={() => setQuickDate(90)}>
                90 dias
              </Button>
              <Button className={classes.quickFilterBtn} onClick={() => setQuickDate(365)}>
                1 año
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>

      {loading && (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      )}

      {!loading && overview && (
        <>
          {/* KPIs */}
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={6} sm={4} md={2}>
              <Card className={classes.kpiCard} elevation={2}>
                <CardContent>
                  <Box className={classes.kpiContent}>
                    <div>
                      <Typography className={classes.kpiValue} color="primary">
                        {overview.totalTickets}
                      </Typography>
                      <Typography className={classes.kpiLabel}>
                        Total Chats
                      </Typography>
                    </div>
                    <AssignmentIcon className={classes.kpiIcon} color="primary" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card className={classes.kpiCard} elevation={2}>
                <CardContent>
                  <Box className={classes.kpiContent}>
                    <div>
                      <Typography className={classes.kpiValue} style={{ color: "#4CAF50" }}>
                        {overview.totalContacts}
                      </Typography>
                      <Typography className={classes.kpiLabel}>
                        Contactos
                      </Typography>
                    </div>
                    <PeopleIcon className={classes.kpiIcon} style={{ color: "#4CAF50" }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card className={classes.kpiCard} elevation={2}>
                <CardContent>
                  <Box className={classes.kpiContent}>
                    <div>
                      <Typography className={classes.kpiValue} style={{ color: "#9C27B0" }}>
                        {overview.totalMessages}
                      </Typography>
                      <Typography className={classes.kpiLabel}>
                        Mensajes
                      </Typography>
                    </div>
                    <ChatIcon className={classes.kpiIcon} style={{ color: "#9C27B0" }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card className={classes.kpiCard} elevation={2}>
                <CardContent>
                  <Box className={classes.kpiContent}>
                    <div>
                      <Typography className={classes.kpiValue} style={{ color: "#2196F3" }}>
                        {overview.totalMessagesSent}
                      </Typography>
                      <Typography className={classes.kpiLabel}>
                        Enviados
                      </Typography>
                    </div>
                    <SendIcon className={classes.kpiIcon} style={{ color: "#2196F3" }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card className={classes.kpiCard} elevation={2}>
                <CardContent>
                  <Box className={classes.kpiContent}>
                    <div>
                      <Typography className={classes.kpiValue} style={{ color: "#FF9800" }}>
                        {overview.totalMessagesReceived}
                      </Typography>
                      <Typography className={classes.kpiLabel}>
                        Recibidos
                      </Typography>
                    </div>
                    <CallReceivedIcon className={classes.kpiIcon} style={{ color: "#FF9800" }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card className={classes.kpiCard} elevation={2}>
                <CardContent>
                  <Box className={classes.kpiContent}>
                    <div>
                      <Typography className={classes.kpiValue} style={{ color: "#F44336" }}>
                        {overview.avgTicketsPerDay}
                      </Typography>
                      <Typography className={classes.kpiLabel}>
                        Chats/dia
                      </Typography>
                    </div>
                    <TrendingUpIcon className={classes.kpiIcon} style={{ color: "#F44336" }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row 1 */}
          <Grid container spacing={3} style={{ marginBottom: 24 }}>
            <Grid item xs={12} md={8}>
              <Paper className={classes.chartPaper} elevation={2}>
                <Typography className={classes.sectionTitle}>
                  <TrendingUpIcon color="primary" /> Chats por dia
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ticketsByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => {
                        try { return format(new Date(d + "T12:00:00"), "dd MMM", { locale: es }); }
                        catch { return d; }
                      }}
                      fontSize={12}
                    />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip
                      labelFormatter={(d) => {
                        try { return format(new Date(d + "T12:00:00"), "dd 'de' MMMM yyyy", { locale: es }); }
                        catch { return d; }
                      }}
                    />
                    <Legend />
                    <Bar dataKey="closed" name="Cerrados" fill="#4CAF50" stackId="a" />
                    <Bar dataKey="open" name="Abiertos" fill="#FF9800" stackId="a" />
                    <Bar dataKey="pending" name="Pendientes" fill="#2196F3" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.chartPaper} elevation={2}>
                <Typography className={classes.sectionTitle}>
                  <AssignmentIcon color="primary" /> Estado de Chats
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={50}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={
                            entry.name === "Abiertos"
                              ? "#FF9800"
                              : entry.name === "Pendientes"
                              ? "#2196F3"
                              : "#4CAF50"
                          }
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Row 2 */}
          <Grid container spacing={3} style={{ marginBottom: 24 }}>
            <Grid item xs={12} md={6}>
              <Paper className={classes.chartPaper} elevation={2}>
                <Typography className={classes.sectionTitle}>
                  <ChatIcon color="primary" /> Mensajes por dia
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={messagesByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => {
                        try { return format(new Date(d + "T12:00:00"), "dd MMM", { locale: es }); }
                        catch { return d; }
                      }}
                      fontSize={12}
                    />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip
                      labelFormatter={(d) => {
                        try { return format(new Date(d + "T12:00:00"), "dd 'de' MMMM yyyy", { locale: es }); }
                        catch { return d; }
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="sent"
                      name="Enviados"
                      stroke="#2196F3"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="received"
                      name="Recibidos"
                      stroke="#FF9800"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper className={classes.chartPaper} elevation={2}>
                <Typography className={classes.sectionTitle}>
                  <PeopleIcon color="primary" /> Chats por Agente
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ticketsByUser} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="userName"
                      width={120}
                      fontSize={12}
                    />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="closed" name="Cerrados" fill="#4CAF50" stackId="a" />
                    <Bar dataKey="open" name="Abiertos" fill="#FF9800" stackId="a" />
                    <Bar dataKey="pending" name="Pendientes" fill="#2196F3" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Detailed Table */}
          <Paper className={classes.tablePaper} elevation={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                <AssignmentIcon color="primary" /> Detalle de Chats ({detailedTickets.length})
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Contacto</strong></TableCell>
                    <TableCell><strong>Numero</strong></TableCell>
                    <TableCell><strong>Agente</strong></TableCell>
                    <TableCell><strong>Estado</strong></TableCell>
                    <TableCell align="center"><strong>Mensajes</strong></TableCell>
                    <TableCell><strong>Ultimo Mensaje</strong></TableCell>
                    <TableCell><strong>Creado</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailedTickets
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((ticket) => (
                      <TableRow key={ticket.ticketId} hover>
                        <TableCell>{ticket.ticketId}</TableCell>
                        <TableCell>{ticket.contactName}</TableCell>
                        <TableCell>{ticket.contactNumber}</TableCell>
                        <TableCell>{ticket.userName}</TableCell>
                        <TableCell>
                          <Chip
                            label={STATUS_LABELS[ticket.status] || ticket.status}
                            size="small"
                            className={classes.statusChip}
                            style={{
                              backgroundColor: STATUS_COLORS[ticket.status] || "#999",
                              color: "#fff",
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">{ticket.messagesCount}</TableCell>
                        <TableCell>
                          {ticket.lastMessage
                            ? ticket.lastMessage.substring(0, 50) +
                              (ticket.lastMessage.length > 50 ? "..." : "")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            try {
                              return format(new Date(ticket.createdAt), "dd/MM/yyyy HH:mm");
                            } catch {
                              return ticket.createdAt;
                            }
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  {detailedTickets.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No hay datos para el rango seleccionado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={detailedTickets.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Filas por pagina:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </Paper>
        </>
      )}
      </Paper>
    </MainContainer>
  );
};

export default Reports;
